import {
  Box,
  Heading,
  VStack,
  Card,
  FormControl,
  FormLabel,
  Text,
  HStack,
  Switch,
  Button,
  Icon,
  Flex,
  useToast
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ThemeToggle } from '../ThemeToggle';
import { DataLoadingPreferences } from '../DataLoadingPreferences';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { useThemeMode } from '../../../hooks/v0/useThemeMode';
import { SubscriptionCard } from './SubscriptionCard';
import { ModelPreferenceSelector } from './ModelPreferenceSelector';
import { useAtom } from 'jotai';
import { fetchSubscriptionAtom } from '../../../atoms/v0/subscriptionAtoms';
import { streamingChatEnabledAtom } from '../../../atoms/v0/chatAtoms';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FiTag, FiChevronRight, FiMessageSquare, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { clearPWACache, forceUpdatePWA, checkForPWAUpdate } from '../../../utils/pwaUtils';
import { Posthog } from '@capawesome/capacitor-posthog';

export const SettingsSection = () => {
  const { t } = useTranslation(['v0/me', 'v0/tasks', 'v0/chat']);
  const { isDarkMode, toggleColorMode } = useThemeMode();
  const [, fetchSubscription] = useAtom(fetchSubscriptionAtom);
  const [streamingChatEnabled, setStreamingChatEnabled] = useAtom(streamingChatEnabledAtom);
  const [isCheckingUpdate, setIsCheckingUpdate] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const toast = useToast();

  // 检查是否显示订阅功能
  useEffect(() => {
    const checkFeatureFlag = async () => {
      try {
        const { enabled } = await Posthog.isFeatureEnabled({
          key: 'enable-stripe-subscription'
        });
        setShowSubscription(enabled);
        console.log('Stripe subscription feature flag:', enabled ? 'enabled' : 'disabled');
      } catch (error) {
        console.error('Error checking feature flag:', error);
        setShowSubscription(false); // 出错时默认隐藏
      }
    };

    checkFeatureFlag();
  }, []);

  // 在组件挂载时获取订阅信息
  useEffect(() => {
    // 只有当功能开关启用时才获取订阅信息
    if (showSubscription) {
      fetchSubscription();
    }
  }, [fetchSubscription, showSubscription]);

  // 检测 PWA 安装状态
  useEffect(() => {
    // 使用统一的平台工具检测
    import('../../../utils/platformUtils').then(({ isStandaloneMode }) => {
      setIsPwaInstalled(isStandaloneMode());
    });
  }, []);

  // 检查 PWA 更新
  const handleCheckUpdate = async () => {
    setIsCheckingUpdate(true);
    try {
      await checkForPWAUpdate();
      setLastChecked(new Date());
      toast({
        title: t('v0/me:pwa.checkComplete', '检查完成'),
        description: t('v0/me:pwa.updateNotAvailable', '当前已是最新版本'),
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('检查更新失败:', error);
      toast({
        title: t('v0/me:pwa.checkUpdateFailed', '检查更新失败'),
        description: t('v0/common:errors.network', '网络错误'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCheckingUpdate(false);
    }
  };

  // 强制更新 PWA
  const handleForceUpdate = async () => {
    try {
      toast({
        title: t('v0/me:pwa.updating', '正在更新'),
        description: t('v0/me:pwa.clearingCache', '正在清除缓存...'),
        status: 'loading',
        duration: 5000,
        isClosable: false,
      });
      await forceUpdatePWA();
    } catch (error) {
      console.error('强制更新失败:', error);
      toast({
        title: t('v0/me:pwa.updateFailed', '更新失败'),
        description: t('v0/common:errors.unknown', '未知错误'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // 清除 PWA 缓存
  const handleClearCache = async () => {
    try {
      const cleared = await clearPWACache();
      if (cleared) {
        toast({
          title: t('v0/me:pwa.cacheCleared', '缓存已清除'),
          description: t('v0/me:pwa.cacheClearedDescription', '应用缓存已成功清除'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        toast({
          title: t('v0/me:pwa.cacheClearFailed', '清除缓存失败'),
          description: t('v0/common:errors.unknown', '未知错误'),
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('清除缓存失败:', error);
      toast({
        title: t('v0/me:pwa.cacheClearFailed', '清除缓存失败'),
        description: t('v0/common:errors.unknown', '未知错误'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Box mt={6}>
      <Heading as="h3" size="md" mb={4}>{t('v0/me:settings')}</Heading>
      <VStack spacing={4} align="stretch">
        {/* 主题设置 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel htmlFor="theme-toggle" mb={0} fontWeight="medium">
                {t('v0/me:uiSettings.themeMode')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {isDarkMode ? t('v0/me:uiSettings.darkMode') : t('v0/me:uiSettings.lightMode')}
              </Text>
            </Box>
            <HStack>
              <Text fontSize="sm">{isDarkMode ? t('v0/me:uiSettings.darkMode') : t('v0/me:uiSettings.lightMode')}</Text>
              <Switch
                id="theme-toggle"
                isChecked={isDarkMode}
                onChange={toggleColorMode}
                colorScheme="teal"
              />
              <ThemeToggle showTooltip={true} />
            </HStack>
          </FormControl>
        </Card>

        {/* 语言设置 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel htmlFor="language-selector" mb={0} fontWeight="medium">
                {t('v0/me:uiSettings.language')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {t('v0/me:uiSettings.languages.title')}
              </Text>
            </Box>
            <LanguageSwitcher variant="full" size="md" />
          </FormControl>
        </Card>

        {/* 流式聊天开关 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <FormLabel htmlFor="streaming-chat-toggle" mb={0} fontWeight="medium">
                {t('v0/chat:streamingChat.title', '流式聊天')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {t('v0/chat:streamingChat.description', '启用实时流式聊天响应')}
              </Text>
            </Box>
            <HStack>
              <Text fontSize="sm">
                {streamingChatEnabled
                  ? t('v0/chat:streamingChat.enabled', '已启用')
                  : t('v0/chat:streamingChat.disabled', '已禁用')}
              </Text>
              <Switch
                id="streaming-chat-toggle"
                isChecked={streamingChatEnabled}
                onChange={() => setStreamingChatEnabled(!streamingChatEnabled)}
                colorScheme="teal"
              />
              <Icon as={FiMessageSquare} color="teal.500" />
            </HStack>
          </FormControl>
        </Card>

        {/* AI 模型偏好设置区域 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <ModelPreferenceSelector />
        </Card>

        {/* 标签管理 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <Flex justify="space-between" align="center">
            <Box>
              <Heading as="h4" size="sm" fontWeight="medium" mb={1}>
                {t('v0/tasks:tags.manageTagsTitle', '管理标签')}
              </Heading>
              <Text fontSize="sm" color="gray.500">
                {t('v0/tasks:tags.manageTagsDescription', '创建、编辑和管理任务标签')}
              </Text>
            </Box>
            <Button
              as={RouterLink}
              to="/tags"
              colorScheme="teal"
              variant="ghost"
              size="sm"
              rightIcon={<Icon as={FiChevronRight} />}
              leftIcon={<Icon as={FiTag} />}
            >
              {t('v0/common:manage', '管理')}
            </Button>
          </Flex>
        </Card>

        {/* PWA 设置 */}
        <Card p={4} borderRadius="lg" boxShadow="sm">
          <Box mb={3}>
            <Heading as="h4" size="sm" fontWeight="medium" mb={1}>
              {t('v0/me:pwa.title', 'PWA 应用设置')}
            </Heading>
            <Text fontSize="sm" color="gray.500">
              {t('v0/me:pwa.description', '管理应用安装、更新和缓存')}
            </Text>
            {lastChecked && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {t('v0/me:pwa.lastChecked', '上次检查')}: {lastChecked.toLocaleString()}
              </Text>
            )}
          </Box>

          <Flex gap={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<Icon as={FiRefreshCw} />}
              colorScheme="blue"
              isLoading={isCheckingUpdate}
              onClick={handleCheckUpdate}
            >
              {t('v0/me:pwa.checkUpdate', '检查更新')}
            </Button>

            <Button
              size="sm"
              leftIcon={<Icon as={FiDownload} />}
              colorScheme="teal"
              onClick={handleForceUpdate}
            >
              {t('v0/me:pwa.forceUpdate', '强制更新')}
            </Button>

            <Button
              size="sm"
              colorScheme="orange"
              variant="outline"
              onClick={handleClearCache}
            >
              {t('v0/me:pwa.clearCache', '清除缓存')}
            </Button>
          </Flex>

          <Text fontSize="xs" color="gray.500" mt={3}>
            {isPwaInstalled
              ? t('v0/me:pwa.installedStatus', '应用已安装为 PWA')
              : t('v0/me:pwa.notInstalledStatus', '应用未安装为 PWA')}
          </Text>
        </Card>

        {/* 订阅卡片 - 根据功能开关显示 */}
        {showSubscription && <SubscriptionCard />}

        <DataLoadingPreferences />
      </VStack>
    </Box>
  );
};